import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Box,
	CircularProgress,
	Avatar,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Radio,
	Chip,
	TextField,
	InputAdornment,
	Grid,
	Card,
	CardContent,
	CardActionArea,
} from '@material-ui/core';
import {LocalShipping, Search} from '@material-ui/icons';
import SportsMotorsportsIcon from '@material-ui/icons/SportsMotorsports';
import {useOrders} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiDialog-paper': {
			minWidth: 700,
			maxWidth: 900,
		},
	},
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	orderInfo: {
		padding: theme.spacing(2),
		backgroundColor: '#f5f7fa',
		borderRadius: 8,
		marginBottom: theme.spacing(2),
	},
	driverGrid: {
		maxHeight: 450,
		overflow: 'auto',
		marginTop: theme.spacing(1),
	},
	driverCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
		cursor: 'pointer',
		transition: 'all 0.15s',
		'&:hover': {
			borderColor: theme.palette.primary.light,
			backgroundColor: '#f5f5f5',
		},
	},
	selectedDriverCard: {
		borderColor: theme.palette.primary.main,
		backgroundColor: '#e3f2fd',
		borderWidth: 2,
	},
	avatar: {
		width: 40,
		height: 40,
		backgroundColor: theme.palette.primary.main,
	},
	searchField: {
		marginBottom: theme.spacing(2),
	},
}));

const DriverAssignDialog = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const alert = useAlert();
	const {driverDialogOpen, selectedOrder, drivers} = state;

	const [selectedDriverId, setSelectedDriverId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [driverType, setDriverType] = useState('primary'); // primary or secondary
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		if (driverDialogOpen && selectedOrder) {
			// Set current driver based on driver type
			if (driverType === 'secondary') {
				setSelectedDriverId(selectedOrder.secondary_driver || null);
			} else {
				setSelectedDriverId(selectedOrder.driver_id || null);
			}
		}
	}, [driverDialogOpen, selectedOrder, driverType]);

	const handleClose = () => {
		setSelectedDriverId(null);
		setDriverType('primary');
		setSearchQuery('');
		actions.closeDriverDialog();
	};

	// Filter drivers based on search query
	const filteredDrivers = drivers.filter((driver) => {
		if (!searchQuery) return true;
		const fullName = `${driver.first_name || ''} ${driver.last_name || ''}`.toLowerCase();
		const phone = (driver.phone || '').toLowerCase();
		const query = searchQuery.toLowerCase();
		return fullName.includes(query) || phone.includes(query);
	});

	const handleAssign = async () => {
		if (!selectedDriverId) {
			alert.warning('Warning', 'Please select a driver');
			return;
		}

		setLoading(true);
		try {
			let response;

			if (driverType === 'secondary') {
				// Use addSecondaryDriver API for secondary driver
				const params = {orderId: selectedOrder.order_id};
				const body = {driver_id: selectedDriverId};
				response = await API.addSecondaryDriver(params, body).then(res => res.json());
			} else {
				// Use updateOrder API for primary driver
				response = await API.updateOrder(
					{orderId: selectedOrder.order_id},
					{driver_id: selectedDriverId}
				).then(res => res.json());
			}

			if (response.success) {
				alert.success('Success', `${driverType === 'secondary' ? 'Secondary' : 'Primary'} driver assigned successfully!`);
				handleClose();
				setTimeout(() => {
					actions.refreshData();
				}, 1000);
			} else {
				alert.error('Error', response.message || 'Failed to assign driver');
			}
		} catch (error) {
			alert.error('Error', 'Error: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveDriver = async () => {
		if (driverType !== 'secondary') {
			alert.warning('Warning', 'Can only remove secondary driver');
			return;
		}

		const result = await alert.confirm(
			'Remove Secondary Driver',
			'Are you sure you want to remove the secondary driver?',
			'Yes, Remove',
			'Cancel'
		);

		if (!result.isConfirmed) {
			return;
		}

		setLoading(true);
		try {
			const params = {orderId: selectedOrder.order_id};
			const body = {driver_id: selectedOrder.secondary_driver};
			const response = await API.removeSecondaryDriver(params, body).then(res => res.json());

			if (response.success) {
				alert.success('Success', 'Secondary driver removed successfully!');
				handleClose();
				setTimeout(() => {
					actions.refreshData();
				}, 1000);
			} else {
				alert.error('Error', response.message || 'Failed to remove driver');
			}
		} catch (error) {
			alert.error('Error', 'Error: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	if (!selectedOrder) return null;

	const currentPrimaryDriver = drivers.find(d => d.id === selectedOrder.driver_id);
	const currentSecondaryDriver = drivers.find(d => d.id === selectedOrder.secondary_driver);
	const hasSecondaryDriver = !!selectedOrder.secondary_driver;

	return (
		<Dialog
			open={driverDialogOpen}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			className={classes.dialog}
		>
			<DialogTitle className={classes.dialogTitle}>
				<Box display="flex" alignItems="center" gap={1}>
					<LocalShipping color="primary"/>
					<Typography variant="h6">Assign Driver</Typography>
				</Box>
			</DialogTitle>

			<DialogContent style={{paddingTop: 24}}>
				{/* Order Info */}
				<Box className={classes.orderInfo}>
					<Typography variant="subtitle2" color="textSecondary">
						Order Details
					</Typography>
					<Typography variant="h6">
						Order #{selectedOrder.order_id}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Customer: {selectedOrder.customer_detail?.name || 'N/A'}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Amount: ${parseFloat(selectedOrder.order_total || 0).toFixed(2)}
					</Typography>
					<Box mt={1} display="flex" gap={1} flexWrap="wrap">
						{currentPrimaryDriver && (
							<Chip
								label={`Primary: ${currentPrimaryDriver.first_name} ${currentPrimaryDriver.last_name}`}
								size="small"
								color="primary"
							/>
						)}
						{currentSecondaryDriver && (
							<Chip
								label={`Secondary: ${currentSecondaryDriver.first_name} ${currentSecondaryDriver.last_name}`}
								size="small"
								color="secondary"
							/>
						)}
					</Box>
				</Box>

				{/* Driver Type Selection */}
				<FormControl fullWidth variant="outlined" size="small" style={{marginBottom: 16}}>
					<InputLabel>Driver Type</InputLabel>
					<Select
						value={driverType}
						onChange={(e) => setDriverType(e.target.value)}
						label="Driver Type"
					>
						<MenuItem value="primary">Primary Driver</MenuItem>
						<MenuItem value="secondary">Secondary Driver</MenuItem>
					</Select>
				</FormControl>

				{/* Search Field */}
				<TextField
					fullWidth
					variant="outlined"
					size="small"
					placeholder="Search by name or phone..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className={classes.searchField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search/>
							</InputAdornment>
						),
					}}
				/>

				{/* Driver Grid */}
				<Typography variant="subtitle2" gutterBottom>
					Select Driver ({filteredDrivers.length} available)
				</Typography>
				<Box className={classes.driverGrid}>
					{filteredDrivers.length === 0 ? (
						<Typography variant="body2" color="textSecondary" align="center" style={{padding: 32}}>
							{searchQuery ? 'No drivers found matching your search' : 'No drivers available'}
						</Typography>
					) : (
						<Grid container spacing={1.5}>
							{filteredDrivers.map((driver) => (
								<Grid item xs={12} sm={6} md={4} key={driver.id}>
									<Box
										onClick={() => setSelectedDriverId(driver.id)}
										className={`${classes.driverCard} ${
											selectedDriverId === driver.id ? classes.selectedDriverCard : ''
										}`}
										p={1.5}
									>
										<Box display="flex" alignItems="center" gap={1.5}>
											<Avatar className={classes.avatar}>
												<SportsMotorsportsIcon/>
											</Avatar>
											<Box flex={1} minWidth={0}>
												<Typography variant="body2" style={{fontWeight: 600}} noWrap>
													{driver.first_name || ''} {driver.last_name || ''}
												</Typography>
												<Typography variant="caption" color="textSecondary" noWrap>
													{driver.phone || 'No phone'}
												</Typography>
											</Box>
											{selectedDriverId === driver.id && (
												<Radio
													checked
													color="primary"
													size="small"
												/>
											)}
										</Box>
									</Box>
								</Grid>
							))}
						</Grid>
					)}
				</Box>
			</DialogContent>

			<DialogActions>
				<Box display="flex" justifyContent="space-between" width="100%">
					<Box>
						{driverType === 'secondary' && hasSecondaryDriver && (
							<Button
								onClick={handleRemoveDriver}
								color="secondary"
								disabled={loading}
							>
								Remove Secondary
							</Button>
						)}
					</Box>
					<Box display="flex" gap={1}>
						<Button onClick={handleClose} disabled={loading}>
							Cancel
						</Button>
						<Button
							onClick={handleAssign}
							color="primary"
							variant="contained"
							disabled={loading || !selectedDriverId}
							startIcon={loading && <CircularProgress size={20}/>}
						>
							{loading ? 'Assigning...' : `Assign ${driverType === 'secondary' ? 'Secondary' : 'Primary'}`}
						</Button>
					</Box>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default DriverAssignDialog;
